import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Avatar,
  useTheme,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  LinearProgress,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Description,
  Assignment,
  CloudUpload,
  MoreVert,
  CheckCircle,
  Warning,
  Schedule,
  ArrowUpward,
  ArrowDownward,
  AttachMoney,
  BusinessCenter,
  NotificationsActive,
  CalendarToday,
  AccountBalanceWallet,
  TrendingUp,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts';

const areaData = [
  { name: 'Jan', documents: 4, questionnaires: 3, revenue: 2400 },
  { name: 'Fév', documents: 3, questionnaires: 4, revenue: 1398 },
  { name: 'Mar', documents: 5, questionnaires: 6, revenue: 9800 },
  { name: 'Avr', documents: 7, questionnaires: 5, revenue: 3908 },
  { name: 'Mai', documents: 6, questionnaires: 8, revenue: 4800 },
  { name: 'Juin', documents: 8, questionnaires: 7, revenue: 3800 },
];

const pieData = [
  { name: 'Complétés', value: 65 },
  { name: 'En cours', value: 25 },
  { name: 'En attente', value: 10 },
];

const revenueData = [
  { name: 'Lun', amount: 1200 },
  { name: 'Mar', amount: 1800 },
  { name: 'Mer', amount: 1600 },
  { name: 'Jeu', amount: 2100 },
  { name: 'Ven', amount: 1900 },
  { name: 'Sam', amount: 1400 },
  { name: 'Dim', amount: 1300 },
];

const tasks = [
  {
    id: 1,
    title: 'Révision contrat',
    status: 'En cours',
    priority: 'Haute',
    deadline: '2024-03-20',
  },
  {
    id: 2,
    title: 'Validation documents',
    status: 'En attente',
    priority: 'Moyenne',
    deadline: '2024-03-22',
  },
  {
    id: 3,
    title: 'Mise à jour statuts',
    status: 'Complété',
    priority: 'Basse',
    deadline: '2024-03-18',
  },
];

const notifications = [
  {
    id: 1,
    title: 'Nouveau document reçu',
    time: 'Il y a 5 minutes',
    type: 'info',
  },
  {
    id: 2,
    title: 'Rappel: Validation requise',
    time: 'Il y a 1 heure',
    type: 'warning',
  },
  {
    id: 3,
    title: 'Document validé',
    time: 'Il y a 2 heures',
    type: 'success',
  },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const StatCard = ({ icon: Icon, title, value, trend, color }) => {
  const theme = useTheme();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.3)',
          height: '100%',
          transition: 'transform 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: `${color}15`,
              color: color,
              width: 48,
              height: 48,
            }}
          >
            <Icon />
          </Avatar>
          <Box sx={{ ml: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Typography
              variant="body2"
              sx={{
                color: trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {trend > 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
              {Math.abs(trend)}%
            </Typography>
          </Box>
        </Box>
      </Paper>
    </motion.div>
  );
};

const TaskList = ({ tasks }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complété':
        return theme.palette.success.main;
      case 'En cours':
        return theme.palette.warning.main;
      default:
        return theme.palette.info.main;
    }
  };

  return (
    <List>
      {tasks.map((task) => (
        <ListItem
          key={task.id}
          sx={{
            mb: 1,
            bgcolor: 'background.paper',
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon>
            <Avatar
              sx={{
                bgcolor: getStatusColor(task.status) + '20',
                color: getStatusColor(task.status),
              }}
            >
              <Assignment />
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={task.title}
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={task.status}
                  size="small"
                  sx={{
                    bgcolor: getStatusColor(task.status) + '20',
                    color: getStatusColor(task.status),
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  <CalendarToday sx={{ fontSize: 12, mr: 0.5 }} />
                  {task.deadline}
                </Typography>
              </Box>
            }
          />
          <IconButton size="small" onClick={handleClick}>
            <MoreVert />
          </IconButton>
        </ListItem>
      ))}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleClose}>Voir les détails</MenuItem>
        <MenuItem onClick={handleClose}>Modifier</MenuItem>
        <MenuItem onClick={handleClose}>Supprimer</MenuItem>
      </Menu>
    </List>
  );
};

const NotificationList = ({ notifications }) => {
  const theme = useTheme();

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      default:
        return <NotificationsActive color="info" />;
    }
  };

  return (
    <List>
      {notifications.map((notification) => (
        <ListItem
          key={notification.id}
          sx={{
            mb: 1,
            bgcolor: 'background.paper',
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ListItemIcon>
            {getNotificationIcon(notification.type)}
          </ListItemIcon>
          <ListItemText
            primary={notification.title}
            secondary={notification.time}
          />
        </ListItem>
      ))}
    </List>
  );
};

const Dashboard = () => {
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.dark}15 100%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Tableau de bord
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              Nouveau document
            </Button>
          </Box>
        </Box>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Grid container spacing={3}>
          {/* Statistiques */}
          <Grid item xs={12} md={3}>
            <StatCard
              icon={Description}
              title="Documents"
              value="24"
              trend={12}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={Assignment}
              title="Questionnaires"
              value="8"
              trend={-5}
              color={theme.palette.secondary.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={AccountBalanceWallet}
              title="Revenue"
              value="15k€"
              trend={8}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={TrendingUp}
              title="Croissance"
              value="85%"
              trend={15}
              color={theme.palette.info.main}
            />
          </Grid>

          {/* Graphique principal */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Aperçu de l'activité</Typography>
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={areaData}>
                    <defs>
                      <linearGradient id="colorDocuments" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorQuestionnaires" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="documents"
                      stroke={theme.palette.primary.main}
                      fillOpacity={1}
                      fill="url(#colorDocuments)"
                    />
                    <Area
                      type="monotone"
                      dataKey="questionnaires"
                      stroke={theme.palette.secondary.main}
                      fillOpacity={1}
                      fill="url(#colorQuestionnaires)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Graphique circulaire */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                height: '100%',
              }}
            >
              <Typography variant="h6" gutterBottom>
                État des documents
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ mt: 2 }}>
                {pieData.map((entry, index) => (
                  <Box
                    key={entry.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: COLORS[index % COLORS.length],
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">
                      {entry.name} ({entry.value}%)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Tâches récentes */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Tâches récentes</Typography>
                <Button size="small" endIcon={<ArrowUpward />}>
                  Voir tout
                </Button>
              </Box>
              <TaskList tasks={tasks} />
            </Paper>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Notifications</Typography>
                <Button size="small" endIcon={<ArrowUpward />}>
                  Voir tout
                </Button>
              </Box>
              <NotificationList notifications={notifications} />
            </Paper>
          </Grid>

          {/* Graphique de revenus */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Revenus hebdomadaires</Typography>
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="amount"
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};

export default Dashboard; 